import React, { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  ArrowDownTrayIcon,
  DocumentTextIcon,
} from "@heroicons/react/24/outline";
import { toast } from "react-toastify";
import Modal from "../common/Modal";
import Loader from "../common/Loader";
import DeviceConnectivityPanel from "./DeviceConnectivityPanel";
import { deviceLogFileService } from "../../api/services/deviceLogFileService";
import {
  DISPLAY_TZ,
  formatDisplayDate,
  formatDisplayDateTime,
} from "../../utils/dateUtils";

interface DeviceLogsModalProps {
  isOpen: boolean;
  onClose: () => void;
  deviceId: string;
  /** Label appended to the modal title (screen name or device code). */
  titleContext?: string;
}

const formatBytes = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatTime = (iso: string): string =>
  new Date(iso).toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: DISPLAY_TZ,
  });

// A day key is an IST calendar day; anchor it in IST so it can't render as
// the previous day.
const formatDayKey = (date: string) => formatDisplayDate(`${date}T12:00:00+05:30`);

const ageLabel = (iso: string): string => {
  const minutes = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `${hours} h ago`;
  return `${Math.round(hours / 24)} days ago`;
};

// Boxes upload every ~10 minutes, so anything well past that means the box
// has stopped shipping (off, offline, or app not running).
const freshnessClass = (iso: string | null): string => {
  if (!iso) return "bg-gray-400";
  const minutes = (Date.now() - new Date(iso).getTime()) / 60000;
  if (minutes <= 25) return "bg-green-500";
  if (minutes <= 120) return "bg-amber-500";
  return "bg-red-500";
};

/**
 * Logs a TV box uploads on its own every ~10 minutes, grouped by day. A day
 * downloads as one text file; single chunks can be downloaded too.
 */
export default function DeviceLogsModal({
  isOpen,
  onClose,
  deviceId,
  titleContext,
}: DeviceLogsModalProps) {
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [downloading, setDownloading] = useState<string | null>(null);

  const summary = useQuery({
    queryKey: ["deviceLogSummary", deviceId],
    queryFn: () => deviceLogFileService.getSummary(deviceId),
    enabled: isOpen && !!deviceId,
    refetchInterval: 60_000,
  });

  const days = summary.data?.days ?? [];

  // Default to the newest day once the summary arrives.
  useEffect(() => {
    if (!selectedDate && days.length > 0) {
      setSelectedDate(days[0].date);
    }
  }, [days, selectedDate]);

  const files = useQuery({
    queryKey: ["deviceLogFiles", deviceId, selectedDate],
    queryFn: () => deviceLogFileService.listFiles(deviceId, selectedDate!),
    enabled: isOpen && !!deviceId && !!selectedDate,
  });

  const handleDownload = async (date: string, fileId?: string) => {
    const key = fileId ?? date;
    setDownloading(key);
    try {
      await deviceLogFileService.download(deviceId, date, fileId);
    } catch (error) {
      console.error("Error downloading device logs:", error);
      toast.error(
        error instanceof Error ? error.message : "Failed to download logs",
      );
    } finally {
      setDownloading(null);
    }
  };

  const lastReceivedAt = summary.data?.lastReceivedAt ?? null;
  const selectedDay = days.find((d) => d.date === selectedDate);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={titleContext ? `Device Logs — ${titleContext}` : "Device Logs"}
      size="xl"
    >
      <DeviceConnectivityPanel deviceId={deviceId} enabled={isOpen} />
      {summary.isLoading ? (
        <div className="flex items-center justify-center py-10">
          <Loader size="small" />
        </div>
      ) : summary.isError ? (
        <div className="py-10 text-center text-sm text-red-500">
          Failed to load device logs.
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1 rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm">
            <div className="flex flex-wrap items-center gap-2">
              <span
                className={`inline-block h-2.5 w-2.5 rounded-full ${freshnessClass(lastReceivedAt)}`}
              />
              <span className="font-medium text-gray-800">
                Last log received:
              </span>
              <span className="text-gray-700">
                {lastReceivedAt
                  ? `${formatDisplayDateTime(lastReceivedAt)} (${ageLabel(lastReceivedAt)})`
                  : "never"}
              </span>
            </div>
            <p className="text-xs text-gray-500">
              The box uploads its logs about every 10 minutes and catches up
              after being offline. If logs keep arriving while the screen
              shows OFF, the box is running and the status reporting is what
              is failing. Logs are kept {summary.data?.retentionDays ?? 30}{" "}
              days. Only boxes on the latest app version send logs.
            </p>
          </div>

          {days.length === 0 ? (
            <div className="py-10 text-center text-gray-500">
              <DocumentTextIcon className="mx-auto mb-2 h-12 w-12 text-gray-400" />
              <p>No logs received from this device yet.</p>
            </div>
          ) : (
            <div className="flex flex-col gap-4 md:flex-row">
              <div className="md:w-56 md:flex-shrink-0">
                <h4 className="mb-2 text-xs font-semibold uppercase text-gray-500">
                  Days
                </h4>
                <ul className="flex max-h-40 flex-col gap-1 overflow-y-auto md:max-h-[50vh]">
                  {days.map((day) => (
                    <li key={day.date}>
                      <button
                        type="button"
                        onClick={() => setSelectedDate(day.date)}
                        className={`w-full rounded px-3 py-2 text-left text-sm ${
                          day.date === selectedDate
                            ? "bg-primary-default text-white"
                            : "text-gray-700 hover:bg-gray-100"
                        }`}
                      >
                        <div className="font-medium">{formatDayKey(day.date)}</div>
                        <div
                          className={`text-xs ${day.date === selectedDate ? "text-white" : "text-gray-500"}`}
                        >
                          {day.fileCount} file{day.fileCount === 1 ? "" : "s"} ·{" "}
                          {formatBytes(day.totalBytes)}
                        </div>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="min-w-0 flex-1">
                {selectedDay && (
                  <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                    <h4 className="text-sm font-semibold text-gray-800">
                      {formatDayKey(selectedDay.date)}
                    </h4>
                    <button
                      type="button"
                      className="inline-flex items-center gap-1.5 rounded bg-primary-default px-3 py-1.5 text-sm text-white hover:bg-primary-light disabled:cursor-not-allowed disabled:opacity-50"
                      disabled={downloading !== null}
                      onClick={() => handleDownload(selectedDay.date)}
                    >
                      <ArrowDownTrayIcon className="h-4 w-4" />
                      {downloading === selectedDay.date
                        ? "Preparing…"
                        : "Download day (.txt)"}
                    </button>
                  </div>
                )}

                {files.isLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader size="small" />
                  </div>
                ) : files.isError ? (
                  <div className="py-8 text-center text-sm text-red-500">
                    Failed to load the files for this day.
                  </div>
                ) : (
                  <div className="max-h-[50vh] overflow-auto rounded border border-gray-200">
                    <table className="min-w-full text-sm">
                      <thead className="sticky top-0 bg-gray-50 text-left text-xs uppercase text-gray-500">
                        <tr>
                          <th className="px-3 py-2">Time (IST)</th>
                          <th className="px-3 py-2">Lines</th>
                          <th className="px-3 py-2">Size</th>
                          <th className="px-3 py-2">App</th>
                          <th className="px-3 py-2" />
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {(files.data ?? []).map((file) => (
                          <tr key={file.id} className="text-gray-700">
                            <td className="whitespace-nowrap px-3 py-2">
                              {formatTime(file.startedAt)} –{" "}
                              {formatTime(file.endedAt)}
                            </td>
                            <td className="px-3 py-2">{file.lineCount}</td>
                            <td className="whitespace-nowrap px-3 py-2">
                              {formatBytes(file.sizeBytes)}
                            </td>
                            <td className="whitespace-nowrap px-3 py-2">
                              {file.appVersion ?? "-"}
                            </td>
                            <td className="px-3 py-2 text-right">
                              <button
                                type="button"
                                title="Download this file"
                                className="rounded p-1 text-gray-600 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
                                disabled={downloading !== null}
                                onClick={() =>
                                  handleDownload(selectedDate!, file.id)
                                }
                              >
                                <ArrowDownTrayIcon className="h-4 w-4" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                <p className="mt-2 text-xs text-gray-400">
                  Times come from the device clock; a box with a wrong clock
                  shows wrong times here too.
                </p>
              </div>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}
